/**
 * One chat in the Messages list.
 *
 * ISSUE-1355: the mark is the chat's photo when one resolved, otherwise an
 * initials plate in the same name-hashed color the chat's people get
 * everywhere else.
 *
 * ISSUE-1362: a trailing More button opens the row's quick actions (Mark as
 * read, Mute or Unmute), the web stand-in for v1's swipe actions. A right
 * click on the row opens the same menu. The More button is a sibling of the
 * row button rather than a child, since a button inside a button is invalid
 * HTML and the inner click never arrives.
 */
import { FOCUS_RING } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { ChatAvatar } from "./ChatAvatar";
import { chatRowActionsLabel } from "./chatListActionsCopy";
import type { typeChatListRow } from "./messagesListState";

export function ChatListRow({
  row,
  photoUrl = null,
  onSelect,
  onOpenActions,
  testId,
}: {
  row: typeChatListRow;
  photoUrl?: string | null;
  onSelect: () => void;
  onOpenActions?: () => void;
  testId?: string;
}): ReactNode {
  return (
    <div
      className="flex w-full items-center gap-1"
      onContextMenu={
        onOpenActions === undefined
          ? undefined
          : (event) => {
              event.preventDefault();
              onOpenActions();
            }
      }
    >
      <button
        type="button"
        onClick={onSelect}
        data-testid={testId}
        className={`flex min-w-0 flex-1 min-h-touch-target items-center gap-3 py-2 text-left ${FOCUS_RING}`}
      >
        <ChatAvatar
          title={row.title}
          photoUrl={photoUrl}
          testId={testId === undefined ? undefined : `${testId}-avatar`}
        />
        <span className="min-w-0 flex-1">
          <p className="truncate text-body-strong text-ink">{row.title}</p>
          <p
            className={`truncate text-body ${row.isStrong ? "text-ink" : "text-ink-secondary"}`}
          >
            {row.previewLine}
          </p>
          {row.isMuted ? (
            <p className="text-caption text-ink-tertiary">Muted</p>
          ) : null}
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-caption text-ink-tertiary">
            {row.timeLabel}
          </span>
          {row.unreadLabel !== null ? (
            <span className="rounded-full bg-primary-solid px-2 text-caption text-on-primary">
              {row.unreadLabel}
            </span>
          ) : null}
        </span>
      </button>
      {onOpenActions !== undefined ? (
        <button
          type="button"
          aria-haspopup="dialog"
          aria-label={chatRowActionsLabel(row.title)}
          data-testid={testId === undefined ? undefined : `${testId}-more`}
          onClick={onOpenActions}
          className={`flex min-h-touch-target min-w-touch-target shrink-0 items-center justify-center rounded-full text-body text-ink-tertiary hover:bg-surface-secondary ${FOCUS_RING}`}
        >
          <span aria-hidden="true">···</span>
        </button>
      ) : null}
    </div>
  );
}
